import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, ChevronLeft, FileKey, Loader2, Save, User } from "lucide-react";

const BLUE = "#2563EB";
const cardM = "bg-white rounded-2xl border border-slate-100 shadow-sm";
const inpM = "w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400";
const lblM = "block text-[11px] font-semibold text-slate-500 mb-1";
const DAY_NAME = { sen: "Senin", sel: "Selasa", rab: "Rabu", kam: "Kamis", jum: "Jumat", sab: "Sabtu", min: "Minggu" };
const STATUS_LABEL = { tetap: "Karyawan Tetap", kontrak: "Kontrak", part_time: "Part-time" };
const RELATIONS = ["Orang Tua", "Pasangan", "Saudara", "Anak", "Lainnya"];
const EDIT_KEYS = ["name", "phone", "nik", "birth_place", "birth_date", "gender", "address",
  "emergency_name", "emergency_relation", "emergency_phone", "photo"];

function Ro({ label, value, testid }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

export default function ProfileScreen({ onBack, extra }) {
  const { refresh } = useAuth();
  const [prof, setProf] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    api.get("/employees/me/profile")
      .then((r) => { setProf(r.data); setForm(Object.fromEntries(EDIT_KEYS.map((k) => [k, r.data[k] ?? ""]))); })
      .catch(() => toast.error("Gagal memuat profil"));
  }, []);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const pickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      setF("photo", data.url);
      toast.success("Foto terunggah — tekan Simpan untuk menerapkan");
    } catch { toast.error("Gagal mengunggah foto"); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama lengkap wajib diisi");
    setSaving(true);
    try {
      await api.put("/employees/me/profile", { ...form, documents: prof?.documents || [] });
      await refresh();
      setProf((s) => ({ ...s, ...form }));
      toast.success("Profil berhasil diperbarui");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan profil"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>;

  const initials = (form.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const workDays = (prof?.work_days || []).map((d) => DAY_NAME[d]).filter(Boolean).join(", ");

  return (
    <div data-testid="screen-profil">
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 sticky top-0 z-30" style={{ background: "#F8FAFC" }} data-testid="profil-header">
        <button data-testid="profil-header-back" onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="flex-1 text-center font-bold text-slate-900 text-base pr-9">Profil Saya</h1>
      </div>
      <div className="px-4 space-y-3.5 pb-6">
        {/* Foto profil */}
        <div className={`${cardM} p-5 flex items-center gap-4`}>
          <div className="relative shrink-0">
            <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xl overflow-hidden border border-slate-200">
              {form.photo ? <img src={form.photo} alt="" className="w-full h-full object-cover" data-testid="profil-photo" /> : (initials || <User className="w-8 h-8" />)}
            </div>
            <button data-testid="profil-photo-btn" onClick={() => photoRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full text-white flex items-center justify-center border-2 border-white" style={{ background: BLUE }}>
              <Camera className="w-4 h-4" />
            </button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-slate-900 text-base truncate" data-testid="profil-display-name">{form.name}</div>
            <div className="text-[11px] text-slate-400">{prof?.role_label || prof?.role} • ID {prof?.employee_id || "—"}</div>
            <div className="text-[10px] text-slate-400 mt-1">Ketuk ikon kamera untuk ganti foto profil</div>
          </div>
        </div>

        {/* Data Pribadi (bisa diubah) */}
        <div className={`${cardM} p-4 space-y-3.5`}>
          <div className="text-xs font-bold text-slate-900">Data Pribadi <span className="text-[10px] font-normal text-emerald-600 ml-1">bisa diubah</span></div>
          <div><label className={lblM}>Nama Lengkap <span className="text-blue-600">*</span></label>
            <input data-testid="profil-name-input" value={form.name} onChange={(e) => setF("name", e.target.value)} className={inpM} /></div>
          <div><label className={lblM}>NIK KTP</label>
            <input data-testid="profil-nik" value={form.nik} onChange={(e) => setF("nik", e.target.value.replace(/\D/g, "").slice(0, 16))} placeholder="16 digit NIK" className={inpM} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lblM}>Tempat Lahir</label>
              <input data-testid="profil-birth-place" value={form.birth_place} onChange={(e) => setF("birth_place", e.target.value)} className={inpM} /></div>
            <div><label className={lblM}>Tanggal Lahir</label>
              <input data-testid="profil-birth-date" type="date" value={form.birth_date} onChange={(e) => setF("birth_date", e.target.value)} className={inpM} /></div>
          </div>
          <div><label className={lblM}>Jenis Kelamin</label>
            <select data-testid="profil-gender" value={form.gender} onChange={(e) => setF("gender", e.target.value)} className={inpM}>
              <option value="">Pilih jenis kelamin</option><option value="perempuan">Perempuan</option><option value="laki-laki">Laki-laki</option>
            </select></div>
          <div><label className={lblM}>Nomor WhatsApp</label>
            <input data-testid="profil-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="08xxxxxxxxxx" className={inpM} /></div>
          <div><label className={lblM}>Alamat Domisili</label>
            <textarea data-testid="profil-address" value={form.address} onChange={(e) => setF("address", e.target.value)} rows={3} placeholder="Alamat lengkap" className={`${inpM} h-auto py-2.5`} /></div>
        </div>

        {/* Kontak Darurat (bisa diubah) */}
        <div className={`${cardM} p-4 space-y-3.5`}>
          <div className="text-xs font-bold text-slate-900">Kontak Darurat <span className="text-[10px] font-normal text-emerald-600 ml-1">bisa diubah</span></div>
          <div><label className={lblM}>Nama Kontak</label>
            <input data-testid="profil-emg-name" value={form.emergency_name} onChange={(e) => setF("emergency_name", e.target.value)} className={inpM} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lblM}>Hubungan</label>
              <select data-testid="profil-emg-relation" value={form.emergency_relation} onChange={(e) => setF("emergency_relation", e.target.value)} className={inpM}>
                <option value="">Pilih hubungan</option>{RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select></div>
            <div><label className={lblM}>Nomor Telepon</label>
              <input data-testid="profil-emg-phone" value={form.emergency_phone} onChange={(e) => setF("emergency_phone", e.target.value)} placeholder="08xxxxxxxxxx" className={inpM} /></div>
          </div>
        </div>

        {/* Pekerjaan & Jadwal (hanya lihat) */}
        <div className={`${cardM} p-4`}>
          <div className="text-xs font-bold text-slate-900 mb-3">Pekerjaan & Jadwal <span className="text-[10px] font-normal text-slate-400 ml-1">hanya admin yang bisa mengubah</span></div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <Ro label="ID Karyawan" value={prof?.employee_id} testid="profil-employee-id" />
            <Ro label="Jabatan" value={prof?.role_label || prof?.role} testid="profil-role" />
            <Ro label="Status Kepegawaian" value={STATUS_LABEL[prof?.employment_status] || prof?.employment_status} testid="profil-status" />
            <Ro label="Bergabung" value={prof?.joined_at ? formatDateID(prof.joined_at) : ""} testid="profil-joined" />
            <Ro label="Email Akun" value={prof?.email} testid="profil-email" />
            <Ro label="Hari Kerja" value={workDays} testid="profil-work-days" />
            <Ro label="Jam Kerja" value={prof?.shift_start ? `${prof.shift_start} – ${prof.shift_end}` : ""} testid="profil-shift" />
            <Ro label={`Gaji ${prof?.salary_type || ""}`} value={formatIDR((prof?.base_salary || 0) + (prof?.allowance || 0))} testid="profil-salary" />
          </div>
        </div>

        {/* Dokumen (lihat saja) */}
        {(prof?.documents || []).length > 0 && (
          <div className={`${cardM} p-4 space-y-2`}>
            <div className="text-xs font-bold text-slate-900">Dokumen <span className="text-[10px] font-normal text-slate-400 ml-1">diunggah oleh admin</span></div>
            {prof.documents.map((d, i) => (
              <a key={i} href={d.url} target="_blank" rel="noreferrer" data-testid={`profil-doc-${i}`} className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-3 py-2.5">
                <FileKey className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-xs font-semibold text-slate-700 truncate">{d.name}</span>
              </a>
            ))}
          </div>
        )}

        <button data-testid="profil-save-btn" onClick={save} disabled={saving}
          className="w-full h-12 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: BLUE }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Perubahan
        </button>

        {extra}
      </div>
    </div>
  );
}
